/**
 * P2.A Augment-Tree — 5 Branches × 5 Nodes = 25 Nodes total. Sequential
 * unlock within each branch (Node 3 not buyable without Node 2). Cost
 * doubles per position: 500, 1000, 2000, 4000, 8000 CD per node, total
 * 15 500 CD per branch (77 500 CD for all 25 nodes).
 *
 * The Augment-Tree replaces the Skill-Web Core ring as the primary stat-
 * multiplier sink. CD investments here are PERMANENT — no respec, no decay.
 * Player progression is gear → augments → aspects (P2.B).
 *
 * Effect application:
 *  - Stat-multipliers (hpMult / defMult / atkMult / critChance / critMult)
 *    apply in `calcPlayerStats` BEFORE equipment + set boni so they stack
 *    multiplicatively.
 *  - Loot / XP / Special-Charge multipliers apply at their respective call
 *    sites (`rollTierLoot`, XP-math, special-charge-tick).
 *  - Utility flags (offlineCapHours, autoFarmerSlots, specialStartCharge)
 *    are read directly by the relevant systems.
 */

export type AugmentBranch = 'survivor' | 'aggressor' | 'hunter' | 'engineer' | 'mystic';

export type AugmentEffect =
  // Stat-multipliers (applied in calcPlayerStats):
  | 'hp_pct'                  // +N% Max HP
  | 'def_pct'                 // +N% Defense
  | 'atk_pct'                 // +N% Attack
  | 'crit_chance'             // +N to critChance (additive, 0..1)
  | 'crit_mult'               // +N to critMult (additive, e.g. +0.25 → 1.75x)
  // Loot / XP (applied at roll sites):
  | 'loot_chance_pct'         // +N% drop chance multiplier
  | 'xp_pct'                  // +N% XP gain
  | 'extra_boss_roll'         // +N extra rollTierLoot rolls on boss kill
  // Special / Skill (applied at combat-tick sites):
  | 'special_charge_pct'      // +N% special charge rate
  | 'skill_cd_pct'            // -N% active-skill cooldown (negative value)
  | 'special_dmg_pct'         // +N% special damage
  | 'special_start_charge'    // Special starts at N% charge on run-start
  // Idle / Utility:
  | 'boost_duration_pct'      // +N% boost duration
  | 'auto_farmer_yield_pct'   // +N% auto-farmer output
  | 'salvage_refund_pct'      // +N% salvage CC/scrap refund
  | 'offline_cap_hours'       // override offline-cap (12h instead of 8h)
  | 'auto_farmer_slots';      // +N concurrent auto-farmer slots

export interface AugmentNode {
  position: 1 | 2 | 3 | 4 | 5;
  nameKey: string;
  /** Each entry is an effect-application. Most nodes have one effect,
   *  capstone nodes (position 5) often bundle two. */
  effects: Array<{ effect: AugmentEffect; value: number }>;
}

export interface AugmentBranchDef {
  id: AugmentBranch;
  nameKey: string;
  /** Lucide icon name from src/ui/components/Icon.tsx registry. */
  iconKey: string;
  themeColor: string;
  nodes: [AugmentNode, AugmentNode, AugmentNode, AugmentNode, AugmentNode];
}

/** Cost to advance from `position-1` to `position` (1-indexed). */
export function augmentCost(position: number): number {
  return 500 * 2 ** (position - 1);
}

export const AUGMENT_BRANCHES: Record<AugmentBranch, AugmentBranchDef> = {
  survivor: {
    id: 'survivor', nameKey: 'augment.survivor', iconKey: 'shield', themeColor: '#4affb0',
    nodes: [
      { position: 1, nameKey: 'augment.survivor.1', effects: [{ effect: 'hp_pct', value: 0.05 }] },
      { position: 2, nameKey: 'augment.survivor.2', effects: [{ effect: 'def_pct', value: 0.05 }] },
      { position: 3, nameKey: 'augment.survivor.3', effects: [{ effect: 'hp_pct', value: 0.08 }] },
      { position: 4, nameKey: 'augment.survivor.4', effects: [{ effect: 'def_pct', value: 0.10 }] },
      { position: 5, nameKey: 'augment.survivor.5', effects: [
        { effect: 'hp_pct', value: 0.12 }, { effect: 'def_pct', value: 0.12 },
      ] },
    ],
  },
  aggressor: {
    id: 'aggressor', nameKey: 'augment.aggressor', iconKey: 'sword', themeColor: '#ff5a1f',
    nodes: [
      { position: 1, nameKey: 'augment.aggressor.1', effects: [{ effect: 'atk_pct', value: 0.05 }] },
      { position: 2, nameKey: 'augment.aggressor.2', effects: [{ effect: 'crit_chance', value: 0.03 }] },
      { position: 3, nameKey: 'augment.aggressor.3', effects: [{ effect: 'atk_pct', value: 0.08 }] },
      { position: 4, nameKey: 'augment.aggressor.4', effects: [{ effect: 'crit_mult', value: 0.25 }] },
      { position: 5, nameKey: 'augment.aggressor.5', effects: [
        { effect: 'atk_pct', value: 0.12 }, { effect: 'crit_chance', value: 0.05 },
      ] },
    ],
  },
  hunter: {
    id: 'hunter', nameKey: 'augment.hunter', iconKey: 'sparkles', themeColor: '#ffd84a',
    nodes: [
      { position: 1, nameKey: 'augment.hunter.1', effects: [{ effect: 'loot_chance_pct', value: 0.05 }] },
      { position: 2, nameKey: 'augment.hunter.2', effects: [{ effect: 'xp_pct', value: 0.10 }] },
      { position: 3, nameKey: 'augment.hunter.3', effects: [{ effect: 'loot_chance_pct', value: 0.08 }] },
      { position: 4, nameKey: 'augment.hunter.4', effects: [{ effect: 'xp_pct', value: 0.15 }] },
      { position: 5, nameKey: 'augment.hunter.5', effects: [{ effect: 'extra_boss_roll', value: 1 }] },
    ],
  },
  engineer: {
    id: 'engineer', nameKey: 'augment.engineer', iconKey: 'beaker', themeColor: '#36e8ff',
    nodes: [
      { position: 1, nameKey: 'augment.engineer.1', effects: [{ effect: 'boost_duration_pct', value: 0.20 }] },
      { position: 2, nameKey: 'augment.engineer.2', effects: [{ effect: 'auto_farmer_yield_pct', value: 0.25 }] },
      { position: 3, nameKey: 'augment.engineer.3', effects: [{ effect: 'salvage_refund_pct', value: 0.30 }] },
      { position: 4, nameKey: 'augment.engineer.4', effects: [{ effect: 'offline_cap_hours', value: 12 }] },
      { position: 5, nameKey: 'augment.engineer.5', effects: [{ effect: 'auto_farmer_slots', value: 1 }] },
    ],
  },
  mystic: {
    id: 'mystic', nameKey: 'augment.mystic', iconKey: 'zap', themeColor: '#c64aff',
    nodes: [
      { position: 1, nameKey: 'augment.mystic.1', effects: [{ effect: 'special_charge_pct', value: 0.15 }] },
      { position: 2, nameKey: 'augment.mystic.2', effects: [{ effect: 'skill_cd_pct', value: 0.10 }] },
      { position: 3, nameKey: 'augment.mystic.3', effects: [{ effect: 'special_dmg_pct', value: 0.20 }] },
      { position: 4, nameKey: 'augment.mystic.4', effects: [{ effect: 'skill_cd_pct', value: 0.15 }] },
      { position: 5, nameKey: 'augment.mystic.5', effects: [{ effect: 'special_start_charge', value: 0.25 }] },
    ],
  },
};

/** Convenience: collect the total value for a single effect across ALL
 *  branches up to the player's current node-positions. Stat-style effects
 *  (hp_pct, atk_pct, ...) are summed; flag-style effects (offline_cap_hours,
 *  special_start_charge) take the MAX. */
export function totalAugmentValue(
  augments: { survivor: number; aggressor: number; hunter: number; engineer: number; mystic: number } | undefined,
  effect: AugmentEffect,
): number {
  if (!augments) return 0;
  const useMax = effect === 'offline_cap_hours' || effect === 'special_start_charge';
  let total = useMax ? 0 : 0;
  for (const branchId of Object.keys(AUGMENT_BRANCHES) as AugmentBranch[]) {
    const branch = AUGMENT_BRANCHES[branchId];
    const owned = augments[branchId] ?? 0;
    for (let i = 0; i < owned; i++) {
      const node = branch.nodes[i];
      for (const eff of node.effects) {
        if (eff.effect !== effect) continue;
        if (useMax) total = Math.max(total, eff.value);
        else total += eff.value;
      }
    }
  }
  return total;
}

/** Default starting state — used by createPlayer + migration. */
export function emptyAugments(): NonNullable<import('./types').Player['augments']> {
  return { survivor: 0, aggressor: 0, hunter: 0, engineer: 0, mystic: 0 };
}
